import { Info } from "lucide-react";
import { Controller } from "react-hook-form";

import { Button } from "@/components/button";
import { FieldError, Label, TextField } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";

import {
  type PersonalInfoFormData,
  usePersonalInfoForm,
} from "../hooks/usePersonalInfoForm";
import { EmailInput } from "./EmailInput";
import { NameInput } from "./NameInput";

interface PersonalInfoStepProps {
  initialData: Partial<PersonalInfoFormData>;
  isLoading?: boolean;
  onComplete: (data: PersonalInfoFormData) => void;
}

const creativeSupportOptions = [
  { id: "Design", label: "Design (branding, graphics, web)" },
  { id: "Video", label: "Video / Motion" },
  { id: "Content", label: "Content / Copywriting" },
  { id: "Strategy", label: "Strategy / Creative Direction" },
  { id: "Ads", label: "Ads / Social Creative" },
  { id: "NotSure", label: "Not sure yet" },
  { id: "Other", label: "Other" },
];

const industryOptions = [
  { id: "eCommerce", label: "eCommerce / Retail" },
  { id: "SaaS", label: "SaaS / Tech" },
  { id: "Health", label: "Health / Wellness" },
  { id: "Finance", label: "Finance / Professional Services" },
  { id: "Media", label: "Media / Entertainment" },
  { id: "Education", label: "Education / Nonprofit" },
  { id: "Other", label: "Other" },
];

export function PersonalInfoStep({
  initialData,
  isLoading = false,
  onComplete,
}: PersonalInfoStepProps) {
  const { control, formState, handleSubmit, watch } =
    usePersonalInfoForm(initialData);

  const creativeSupportType = watch("creativeSupportType");
  const industry = watch("industry");

  const onSubmit = (data: PersonalInfoFormData) => {
    onComplete(data);
  };

  const isFormValid = formState.isValid;

  return (
    <form className="flex flex-col gap-4xl" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex ">
        <div className="mr-4 w-10 h-10 rounded-full bg-[#EAE2FF] flex items-center justify-center shrink-0">
          <Info className="size-5 text-[#6938EF]" />
        </div>

        <div>
          <h3 className="text-[14px] font-semibold">
            Kickstarter allows you to test drive NLC
          </h3>
          <p className="text-[14px]">
            Build your brand profile, explore the workflow, and get comfortable
            with creating briefs. You’ll be fully set up and ready for a fast,
            seamless launch the moment you subscribe.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-xl">
        <Controller
          control={control}
          name="fullName"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              isRequired
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Full name</Label>
              <NameInput
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                onClear={() => onChange("")}
                placeholder="Enter your full name"
                ref={ref}
                showClearButton={value.length > 0}
                value={value}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="companyName"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              isRequired
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Company name</Label>
              <NameInput
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                onClear={() => onChange("")}
                placeholder="Enter your company name"
                ref={ref}
                showClearButton={value.length > 0}
                value={value}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="email"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              isRequired
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Email</Label>
              <EmailInput
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                onClear={() => onChange("")}
                placeholder="Enter your email"
                ref={ref}
                showClearButton={value.length > 0}
                value={value}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="creativeSupportType"
          render={({
            field: { onChange, value },
            fieldState: { error, invalid },
          }) => (
            <TextField isInvalid={invalid} isRequired validationBehavior="aria">
              <Label>
                What type of creative support do you currently need?
              </Label>
              <Select
                onSelectionChange={(key) => onChange(key as string)}
                placeholder="Select an option"
                selectedKey={value}
              >
                <SelectButton />
                <SelectPopover>
                  <SelectListBox items={creativeSupportOptions}>
                    {(item) => (
                      <SelectListItem id={item.id}>{item.label}</SelectListItem>
                    )}
                  </SelectListBox>
                </SelectPopover>
              </Select>
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        {creativeSupportType === "Other" && (
          <Controller
            control={control}
            name="creativeSupportOther"
            render={({
              field: { name, onBlur, onChange, ref, value },
              fieldState: { error, invalid },
            }) => (
              <TextField
                isInvalid={invalid}
                isRequired
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                validationBehavior="aria"
                value={value || ""}
              >
                <Label>Please specify</Label>
                <NameInput
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  onClear={() => onChange("")}
                  placeholder="Enter the type of creative support"
                  ref={ref}
                  showClearButton={(value || "").length > 0}
                  value={value || ""}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
        )}

        <Controller
          control={control}
          name="industry"
          render={({
            field: { onChange, value },
            fieldState: { error, invalid },
          }) => (
            <TextField isInvalid={invalid} isRequired validationBehavior="aria">
              <Label>What industry do you serve?</Label>
              <Select
                onSelectionChange={(key) => onChange(key as string)}
                placeholder="Select an option"
                selectedKey={value}
              >
                <SelectButton />
                <SelectPopover>
                  <SelectListBox items={industryOptions}>
                    {(item) => (
                      <SelectListItem id={item.id}>{item.label}</SelectListItem>
                    )}
                  </SelectListBox>
                </SelectPopover>
              </Select>
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        {industry === "Other" && (
          <Controller
            control={control}
            name="industryOther"
            render={({
              field: { name, onBlur, onChange, ref, value },
              fieldState: { error, invalid },
            }) => (
              <TextField
                isInvalid={invalid}
                isRequired
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                validationBehavior="aria"
                value={value || ""}
              >
                <Label>Please specify</Label>
                <NameInput
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  onClear={() => onChange("")}
                  placeholder="Enter your industry"
                  ref={ref}
                  showClearButton={(value || "").length > 0}
                  value={value || ""}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
        )}
      </div>

      <div className="flex flex-col gap-md">
        <Button
          className="w-full"
          isDisabled={isLoading || !isFormValid}
          type="submit"
        >
          {isLoading ? "Creating account..." : "Continue"}
        </Button>

        <p className="text-xs text-text-secondary text-center">
          By continuing, you agree to receive emails from NoLimit Creatives
          about your account and services.
        </p>
      </div>
    </form>
  );
}
